import {
  fetchListing,
  fetchProviders,
  type ProviderSummary,
  type VideoSummary,
} from "./api.ts";
import { categoryTitle } from "./categories.ts";
import {
  emptyState,
  errorState,
  posterCard,
  skeletonGrid,
} from "./components.ts";
import { el } from "./dom.ts";
import type { AppContext } from "./views.ts";
import { visibleProviders } from "./visibility.ts";

/** Providers asked per wave. */
const WAVE_SIZE = 6;
/** Waves allowed on the wire at once: 18 provider requests in flight. */
const WAVES_IN_FLIGHT = 3;
/** /new and /trending page in blocks of this size. */
const PAGE_SIZE = 48;
/** Fetching stops at this many pages per provider, so a looping engine cannot spin forever. */
const MAX_PAGES_PER_PROVIDER = 6;
/** Provider requests in flight on /new. */
const PAGE_CONCURRENCY = 18;
/** Providers sampled for the stats page; enough for a spread without a crawl. */
const DEPTH_SAMPLE = 24;
/** Titles counted per sampled provider on the stats page. */
const DEPTH_LIMIT = 48;
/** No single provider may hold a page open longer than this. */
const PROVIDER_DEADLINE_MS = 12000;
/** A signal that never aborts, for requests with no page lifecycle. */
const NEVER_ABORT = new AbortController().signal;

function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let index = 0; index < items.length; index += size) out.push(items.slice(index, index + size));
  return out;
}

/** Run `task` over `items` with at most `limit` promises open. */
async function eachLimited<T>(
  items: T[],
  limit: number,
  task: (item: T) => Promise<void>,
): Promise<void> {
  let cursor = 0;
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    for (;;) {
      const index = cursor;
      cursor += 1;
      if (index >= items.length) return;
      await task(items[index]);
    }
  });
  await Promise.all(workers);
}

/** Abort one provider request on the caller's signal or after a deadline. */
function withDeadline(
  parent: AbortSignal,
  ms: number,
): { signal: AbortSignal; done: () => void } {
  const controller = new AbortController();
  const forward = (): void => controller.abort();
  if (parent.aborted) controller.abort();
  else parent.addEventListener("abort", forward, { once: true });
  const timer = window.setTimeout(() => controller.abort(), ms);
  return {
    signal: controller.signal,
    done: () => {
      window.clearTimeout(timer);
      parent.removeEventListener("abort", forward);
    },
  };
}

/**
 * Working providers that can list titles, preferred first. The network pages
 * use the same catalogue every other surface uses, so hidden categories and
 * switched-off providers never leak in.
 */
function listableProviders(context: AppContext): ProviderSummary[] {
  const preferred = new Set(context.preferred);
  return visibleProviders(context.providers)
    .filter((provider) => provider.state !== "offline" && provider.capabilities.list)
    .sort(
      (left, right) =>
        Number(preferred.has(right.id)) - Number(preferred.has(left.id)) ||
        left.name.localeCompare(right.name),
    );
}

/**
 * Titles repeat across a network that mirrors the same release under different
 * studio prefixes, emoji and punctuation. The identity key drops the studio
 * bracket, case, accents, emoji and every non-letter, so "Naomi would in 4K 🤓"
 * and "NAOMI WOULD IN 4K" are one title.
 */
function titleKey(raw: string): string {
  return raw
    .replace(/^\s*\[[^\]]*\]\s*[-–—:]?\s*/, "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\p{Extended_Pictographic}/gu, " ")
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim();
}

const MONTH_INDEX: Record<string, number> = {
  jan: 0,
  feb: 1,
  mar: 2,
  apr: 3,
  may: 4,
  jun: 5,
  jul: 6,
  aug: 7,
  sep: 8,
  oct: 9,
  nov: 10,
  dec: 11,
};

/**
 * Providers publish dates in a dozen shapes, or not at all. Anything that can
 * be understood becomes a timestamp; anything else stays null and sorts last.
 */
function publishedAt(raw?: string): number | null {
  if (!raw) return null;
  const value = raw.trim();
  if (!value) return null;
  const direct = Date.parse(value);
  if (Number.isFinite(direct)) return direct;

  const dayFirst = /^(\d{1,2})(?:st|nd|rd|th)?\s+([A-Za-z]{3,9})\.?,?\s+(\d{4})$/i.exec(value);
  if (dayFirst) {
    const month = MONTH_INDEX[dayFirst[2].slice(0, 3).toLowerCase()];
    if (month !== undefined) return Date.UTC(Number(dayFirst[3]), month, Number(dayFirst[1]));
  }
  const monthFirst = /^([A-Za-z]{3,9})\.?\s+(\d{1,2})(?:st|nd|rd|th)?,?\s+(\d{4})$/i.exec(value);
  if (monthFirst) {
    const month = MONTH_INDEX[monthFirst[1].slice(0, 3).toLowerCase()];
    if (month !== undefined) return Date.UTC(Number(monthFirst[3]), month, Number(monthFirst[2]));
  }
  if (/^(today|just now)$/i.test(value)) return Date.now();
  if (/^yesterday$/i.test(value)) return Date.now() - 24 * 60 * 60 * 1000;
  const relative = /^(\d+)\s*(second|minute|hour|day|week|month|year)s?\s+ago$/i.exec(value);
  if (relative) {
    const unit = relative[2].toLowerCase();
    const scales: Record<string, number> = {
      second: 1000,
      minute: 60 * 1000,
      hour: 60 * 60 * 1000,
      day: 24 * 60 * 60 * 1000,
      week: 7 * 24 * 60 * 60 * 1000,
      month: 30 * 24 * 60 * 60 * 1000,
      year: 365 * 24 * 60 * 60 * 1000,
    };
    return Date.now() - Number(relative[1]) * (scales[unit] ?? 0);
  }
  return null;
}

/** "07:25" → 445, "1:03:46" → 3826, "117:54" → 7074; unknown → -1. */
function durationSeconds(raw?: string): number {
  if (!raw) return -1;
  const value = raw.trim().toLowerCase();
  const clock = /^(\d{1,4}):(\d{1,2})(?::(\d{1,2}))?$/.exec(value);
  if (clock) {
    const first = Number(clock[1]);
    const second = Number(clock[2]);
    const third = clock[3] === undefined ? null : Number(clock[3]);
    return third === null ? first * 60 + second : first * 3600 + second * 60 + third;
  }
  const spelled = /(?:(\d+)\s*h)?\s*(?:(\d+)\s*m(?:in)?)?\s*(?:(\d+)\s*s)?/.exec(value);
  if (spelled && (spelled[1] || spelled[2] || spelled[3])) {
    return (
      Number(spelled[1] ?? 0) * 3600 +
      Number(spelled[2] ?? 0) * 60 +
      Number(spelled[3] ?? 0)
    );
  }
  return -1;
}

interface MergedTitle {
  video: VideoSummary;
  key: string;
  /** Publication time, or null when the provider publishes none. */
  stamp: number | null;
  /** Position inside its provider's newest page, 0 first. */
  rank: number;
  /** Position of the provider in the fetch order. */
  providerOrder: number;
}

/**
 * Merge rule shared by /new and /trending.
 *
 * One entry per normalised title. When two providers carry the same title the
 * newest dated copy wins; undated copies keep the first provider in the fetch
 * order. The merged list is ordered by publication time descending, then by
 * the title's position in its provider's own page, then by provider order.
 * Undated titles follow dated ones; they are never given an invented date.
 */
function mergeTitles(entries: Iterable<MergedTitle>): MergedTitle[] {
  const byKey = new Map<string, MergedTitle>();
  for (const entry of entries) {
    const existing = byKey.get(entry.key);
    if (!existing) {
      byKey.set(entry.key, entry);
      continue;
    }
    if ((entry.stamp ?? -Infinity) > (existing.stamp ?? -Infinity)) byKey.set(entry.key, entry);
  }
  return [...byKey.values()].sort((left, right) => {
    const a = left.stamp ?? -Infinity;
    const b = right.stamp ?? -Infinity;
    if (a !== b) return b - a;
    if (left.rank !== right.rank) return left.rank - right.rank;
    if (left.providerOrder !== right.providerOrder) return left.providerOrder - right.providerOrder;
    return left.key.localeCompare(right.key);
  });
}

interface ProviderItems {
  provider: ProviderSummary;
  items: VideoSummary[];
}

/**
 * Ask providers in fixed waves: six per wave, three waves at once. Every
 * answer is reported the moment it lands, so a band fills from whichever
 * providers are quick instead of waiting for a wave's slowest member. A
 * provider that throws contributes an empty page.
 */
async function fetchInWaves(
  providers: ProviderSummary[],
  options: {
    sort: "new" | "popular";
    limit: number;
    signal: AbortSignal;
    onProvider?: (entry: ProviderItems) => void;
    onWave?: (wave: ProviderItems[]) => void;
  },
): Promise<void> {
  const waves = chunk(providers, WAVE_SIZE);
  let cursor = 0;
  const worker = async (): Promise<void> => {
    for (;;) {
      const index = cursor;
      cursor += 1;
      if (index >= waves.length) return;
      const wave = await Promise.all(
        waves[index].map(async (provider): Promise<ProviderItems> => {
          const request = withDeadline(options.signal, PROVIDER_DEADLINE_MS);
          let result: ProviderItems;
          try {
            const items = await fetchListing(
              provider.id,
              { sort: options.sort, limit: options.limit },
              request.signal,
            );
            result = { provider, items };
          } catch {
            result = { provider, items: [] };
          } finally {
            request.done();
          }
          if (!options.signal.aborted) options.onProvider?.(result);
          return result;
        }),
      );
      if (options.signal.aborted) return;
      options.onWave?.(wave);
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(WAVES_IN_FLIGHT, waves.length) }, () => worker()),
  );
}

function networkTabs(context: AppContext, current: string): HTMLElement {
  const tabs: { id: string; label: string }[] = [
    { id: "new", label: "New" },
    { id: "trending", label: "Trending" },
    { id: "stats", label: "Stats" },
  ];
  const nav = el("nav", { class: "feed-tabs", attrs: { "aria-label": "Network views" } });
  for (const tab of tabs) {
    const link = el("a", {
      class: "tag feed-tab",
      attrs: { href: `#/${tab.id}`, "aria-current": tab.id === current ? "page" : "false" },
      text: tab.label,
    });
    link.addEventListener("click", (event) => {
      event.preventDefault();
      context.navigate(`#/${tab.id}`);
    });
    nav.append(link);
  }
  return nav;
}

interface FeedFilters {
  category: string;
  provider: string;
}

/** /new: the whole network's newest titles, filtered, sorted and paged. */
export function newView(context: AppContext): HTMLElement {
  const providers = listableProviders(context);
  const names = Object.fromEntries(
    context.providers.map((provider) => [provider.id, provider.name] as const),
  );
  const providerOrder = new Map(providers.map((provider, index) => [provider.id, index]));
  const categories = [...new Set(providers.map((provider) => provider.category))];

  const filters: FeedFilters = { category: "all", provider: "all" };
  let sort: "new" | "duration" = "new";
  let target = PAGE_SIZE;
  let byKey = new Map<string, MergedTitle>();
  let merged: MergedTitle[] = [];
  let queue: { provider: ProviderSummary; page: number }[] = [];
  let loading = false;
  let exhausted = false;
  let token = 0;
  let controller: AbortController | null = null;

  const root = el("div", { class: "view view--feed" });
  const grid = el("ul", { class: "grid grid--dense", attrs: { "aria-label": "Newest titles", "aria-busy": "true" } });
  grid.append(...skeletonGrid(12, "Loading the newest titles").children);
  const results = el("div", { class: "feed-results" }, grid);
  const summary = el("p", { class: "feed-summary", attrs: { role: "status" } });
  const more = el("button", { class: "btn btn-secondary", attrs: { type: "button" }, text: "Load more" });
  more.addEventListener("click", () => {
    target += PAGE_SIZE;
    render();
    void fill();
  });
  const moreRow = el("div", { class: "view-foot" }, more);

  const scoped = (): ProviderSummary[] => {
    if (filters.provider !== "all") {
      return providers.filter((provider) => provider.id === filters.provider);
    }
    return filters.category === "all"
      ? providers
      : providers.filter((provider) => provider.category === filters.category);
  };

  const sortedPool = (): MergedTitle[] => {
    if (sort === "new") return merged;
    return [...merged].sort((left, right) => {
      const a = durationSeconds(left.video.duration);
      const b = durationSeconds(right.video.duration);
      return b - a;
    });
  };

  let painted = "";
  const render = (): void => {
    const items = sortedPool().slice(0, target);
    if (items.length === 0) {
      // Keep the skeleton promise while the first pages are still in flight.
      summary.textContent = loading ? "Loading the newest titles…" : `${merged.length} merged titles`;
      grid.setAttribute("aria-busy", String(loading));
      moreRow.hidden = true;
      more.disabled = loading;
      return;
    }
    const signature = `${sort}|${items.map((entry) => entry.key).join("\u0001")}`;
    if (signature !== painted) {
      painted = signature;
      grid.replaceChildren(
        ...items.map((entry) =>
          posterCard(entry.video, {
            providerLabel: names[entry.video.provider] ?? entry.video.provider,
            onOpen: (video) =>
              context.navigate(
                `#/watch/${encodeURIComponent(video.provider)}/${encodeURIComponent(video.id)}`,
              ),
          }),
        ),
      );
    }
    grid.setAttribute("aria-busy", String(loading));
    const scope = scoped().length;
    summary.textContent = `${items.length} of ${merged.length} merged titles · ${scope} ${
      scope === 1 ? "provider" : "providers"
    } in scope`;
    moreRow.hidden = exhausted && items.length >= merged.length;
    // A batch can still be settling while the requested page is already full;
    // the next click only needs queued pages, not the whole batch.
    more.disabled = loading && merged.length < target;
  };

  const fill = async (): Promise<void> => {
    if (loading) return;
    const runToken = token;
    const run = new AbortController();
    controller = run;
    loading = true;
    render();
    try {
      while (merged.length < target && queue.length > 0) {
        const batch = queue.splice(0, PAGE_CONCURRENCY);
        await Promise.all(
          batch.map(async ({ provider, page }) => {
            const request = withDeadline(run.signal, PROVIDER_DEADLINE_MS);
            try {
              const items = await fetchListing(
                provider.id,
                { page, sort: "new", limit: PAGE_SIZE },
                request.signal,
              );
              if (runToken !== token) return;
              if (items.length === 0) return;
              let added = 0;
              items.forEach((video, rank) => {
                const key = titleKey(video.title) || `${provider.id}:${video.id}`;
                const entry: MergedTitle = {
                  video: { ...video, provider: provider.id },
                  key,
                  stamp: publishedAt(video.date),
                  rank,
                  providerOrder: providerOrder.get(provider.id) ?? 0,
                };
                const existing = byKey.get(key);
                if (!existing || (entry.stamp ?? -Infinity) > (existing.stamp ?? -Infinity)) {
                  byKey.set(key, entry);
                  added += 1;
                }
              });
              if (items.length === PAGE_SIZE && page < MAX_PAGES_PER_PROVIDER && added > 0) {
                queue.push({ provider, page: page + 1 });
              }
              merged = mergeTitles(byKey.values());
              render();
            } catch {
              /* A provider that fails is dropped from this page's queue. */
            } finally {
              request.done();
            }
          }),
        );
        if (runToken !== token) return;
      }
      if (merged.length < target) exhausted = true;
    } finally {
      if (runToken === token) {
        loading = false;
        controller = null;
        if (merged.length === 0) {
          painted = "";
          grid.replaceChildren();
          results.append(
            emptyState({
              title: "No titles came back.",
              body: "Every provider in this filter failed or returned an empty page.",
              action: {
                label: "Reset filters",
                onOpen: () => {
                  filters.category = "all";
                  filters.provider = "all";
                  paintCategories();
                  paintProviders();
                  reset();
                },
              },
            }),
          );
        }
        render();
        // A click that arrived during the batch may have raised the target.
        if (merged.length < target && queue.length > 0) void fill();
      }
    }
  };

  const categoryRow = el("div", {
    class: "tag-row",
    attrs: { role: "group", "aria-label": "Filter by category" },
  });
  const providerRow = el("div", {
    class: "tag-row tag-row--scroll",
    attrs: { role: "group", "aria-label": "Filter by provider" },
  });
  const sortRow = el("div", {
    class: "tag-row",
    attrs: { role: "group", "aria-label": "Sort titles" },
  });

  const paintCategories = (): void => {
    categoryRow.replaceChildren();
    const entries = [{ id: "all", label: "All categories" }].concat(
      categories.map((category) => ({ id: category, label: categoryTitle(category) })),
    );
    for (const entry of entries) {
      const button = el("button", {
        class: "tag",
        attrs: { type: "button", "aria-pressed": String(entry.id === filters.category) },
        text: entry.label,
      });
      button.addEventListener("click", () => {
        if (filters.category === entry.id) return;
        filters.category = entry.id;
        filters.provider = "all";
        paintCategories();
        paintProviders();
        reset();
      });
      categoryRow.append(button);
    }
  };

  const paintProviders = (): void => {
    providerRow.replaceChildren();
    const scopedProviders =
      filters.category === "all"
        ? providers
        : providers.filter((provider) => provider.category === filters.category);
    const entries = [{ id: "all", label: "All providers" }].concat(
      scopedProviders.map((provider) => ({ id: provider.id, label: provider.name })),
    );
    for (const entry of entries) {
      const button = el("button", {
        class: "tag",
        attrs: { type: "button", "aria-pressed": String(entry.id === filters.provider) },
        text: entry.label,
      });
      button.addEventListener("click", () => {
        if (filters.provider === entry.id) return;
        filters.provider = entry.id;
        paintProviders();
        reset();
      });
      providerRow.append(button);
    }
  };

  const paintSort = (): void => {
    sortRow.replaceChildren();
    const entries: { id: "new" | "duration"; label: string }[] = [
      { id: "new", label: "Newest" },
      { id: "duration", label: "Longest" },
    ];
    for (const entry of entries) {
      const button = el("button", {
        class: "tag",
        attrs: { type: "button", "aria-pressed": String(sort === entry.id) },
        text: entry.label,
      });
      button.addEventListener("click", () => {
        if (sort === entry.id) return;
        sort = entry.id;
        paintSort();
        render();
      });
      sortRow.append(button);
    }
  };

  const reset = (): void => {
    token += 1;
    controller?.abort();
    loading = false;
    exhausted = false;
    target = PAGE_SIZE;
    byKey = new Map();
    merged = [];
    painted = "";
    queue = scoped().map((provider) => ({ provider, page: 1 }));
    results.querySelector(".empty")?.remove();
    grid.replaceChildren(...skeletonGrid(12, "Loading the newest titles").children);
    render();
    void fill();
  };

  root.append(
    el(
      "header",
      { class: "page-head container" },
      el(
        "div",
        { class: "page-head-copy" },
        el("h1", { class: "page-title", text: "New" }),
        el("p", {
          class: "page-sub",
          text: `Newest releases from ${providers.length} working providers, merged into one list.`,
        }),
      ),
      networkTabs(context, "new"),
    ),
    el(
      "div",
      { class: "container feed-body" },
      el(
        "div",
        { class: "feed-filters" },
        el(
          "div",
          { class: "filter-row" },
          el("span", { class: "filter-label", text: "Category" }),
          categoryRow,
        ),
        el(
          "div",
          { class: "filter-row" },
          el("span", { class: "filter-label", text: "Provider" }),
          providerRow,
        ),
        el("div", { class: "filter-row" }, el("span", { class: "filter-label", text: "Sort" }), sortRow),
      ),
      summary,
      results,
      moreRow,
    ),
  );

  paintCategories();
  paintProviders();
  paintSort();
  reset();
  return root;
}

/**
 * /trending: each provider's most-viewed listing, interleaved by rank — every
 * provider's #1 first, then every #2 — and deduplicated by the same title key.
 * Rank numbers are the merged position, and each card keeps its provider chip.
 */
export function trendingView(context: AppContext): HTMLElement {
  const providers = listableProviders(context);
  const names = Object.fromEntries(
    context.providers.map((provider) => [provider.id, provider.name] as const),
  );
  const fetched = new Set<string>();
  const rows = new Map<string, VideoSummary[]>();
  let visible = PAGE_SIZE;
  let finished = false;

  const root = el("div", { class: "view view--trending" });
  const grid = el("ul", {
    class: "grid grid--dense",
    attrs: { "aria-label": "Most-viewed titles across the network", "aria-busy": "true" },
  });
  grid.append(...skeletonGrid(12, "Loading trending titles").children);
  const results = el("div", { class: "feed-results" }, grid);
  const summary = el("p", { class: "feed-summary", attrs: { role: "status" } });
  const more = el("button", { class: "btn btn-secondary", attrs: { type: "button" }, text: "Show more" });
  const moreRow = el("div", { class: "view-foot" }, more);

  const merged = (): VideoSummary[] => {
    const seen = new Set<string>();
    const out: VideoSummary[] = [];
    const deepest = Math.max(0, ...providers.map((provider) => rows.get(provider.id)?.length ?? 0));
    for (let rank = 0; rank < deepest; rank += 1) {
      for (const provider of providers) {
        const item = rows.get(provider.id)?.[rank];
        if (!item) continue;
        const key = titleKey(item.title) || `${provider.id}:${item.id}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push({ ...item, provider: provider.id });
      }
    }
    return out;
  };

  let painted = "";
  const render = (): void => {
    const items = merged().slice(0, visible);
    if (items.length === 0 && !finished) {
      summary.textContent = `Ranking ${fetched.size} of ${providers.length} providers…`;
      return;
    }
    const signature = items
      .map((video) => `${video.provider}:${video.id}`)
      .join("\u0001");
    if (signature === painted) {
      summary.textContent = `${fetched.size} of ${providers.length} providers ranked · ${items.length} titles`;
      return;
    }
    painted = signature;
    grid.replaceChildren(
      ...items.map((video, index) =>
        posterCard(video, {
          rank: index + 1,
          providerLabel: names[video.provider] ?? video.provider,
          onOpen: (entry) =>
            context.navigate(
              `#/watch/${encodeURIComponent(entry.provider)}/${encodeURIComponent(entry.id)}`,
            ),
        }),
      ),
    );
    grid.setAttribute("aria-busy", String(!finished));
    summary.textContent = `${fetched.size} of ${providers.length} providers ranked · ${items.length} titles`;
    moreRow.hidden = finished && items.length >= merged().length;
  };

  more.addEventListener("click", () => {
    visible += PAGE_SIZE;
    render();
  });

  let frame = 0;
  const scheduleRender = (): void => {
    if (frame !== 0) return;
    frame = window.requestAnimationFrame(() => {
      frame = 0;
      if (root.isConnected) render();
    });
  };

  void (async () => {
    const run = new AbortController();
    await fetchInWaves(providers, {
      sort: "popular",
      limit: 3,
      signal: run.signal,
      onProvider: ({ provider, items }) => {
        if (!root.isConnected) {
          run.abort();
          return;
        }
        fetched.add(provider.id);
        if (items.length > 0) rows.set(provider.id, items);
        scheduleRender();
      },
    });
    finished = true;
    if (merged().length === 0) {
      grid.replaceChildren();
      results.append(
        errorState("No provider returned a popular listing.", () => {
          location.hash = "#/trending";
          window.dispatchEvent(new HashChangeEvent("hashchange"));
        }),
      );
    }
    render();
  })();

  root.append(
    el(
      "header",
      { class: "page-head container" },
      el(
        "div",
        { class: "page-head-copy" },
        el("h1", { class: "page-title", text: "Trending" }),
        el("p", {
          class: "page-sub",
          text: "Each provider's most-viewed titles, merged into one ranking.",
        }),
      ),
      networkTabs(context, "trending"),
    ),
    el("div", { class: "container feed-body" }, summary, results, moreRow),
  );
  return root;
}

function bar(value: number, max: number): HTMLElement {
  const track = el("div", { class: "stat-track", attrs: { "aria-hidden": "true" } });
  const fill = el("div", { class: "stat-fill" });
  fill.style.width = max > 0 ? `${Math.max(2, Math.round((value / max) * 100))}%` : "0%";
  track.append(fill);
  return track;
}

interface DepthSample {
  provider: ProviderSummary;
  titles: number;
}

/** One provider per category per round, so the sample spans the catalogue. */
function spreadSample(providers: ProviderSummary[], size: number): ProviderSummary[] {
  const buckets = new Map<string, ProviderSummary[]>();
  for (const provider of providers) {
    const bucket = buckets.get(provider.category) ?? [];
    bucket.push(provider);
    buckets.set(provider.category, bucket);
  }
  const picked: ProviderSummary[] = [];
  const groups = [...buckets.values()];
  for (let round = 0; picked.length < size; round += 1) {
    let added = 0;
    for (const bucket of groups) {
      const provider = bucket[round];
      if (!provider || picked.length >= size) continue;
      picked.push(provider);
      added += 1;
    }
    if (added === 0) break;
  }
  return picked;
}

/** /stats: catalogue counts read from /api/providers, plus a sampled page size. */
export function statsView(context: AppContext): HTMLElement {
  const root = el("div", { class: "view view--stats" });
  const body = el("div", { class: "container stats-body" });
  root.append(
    el(
      "header",
      { class: "page-head container" },
      el(
        "div",
        { class: "page-head-copy" },
        el("h1", { class: "page-title", text: "Stats" }),
        el("p", { class: "page-sub", text: "The catalogue, counted from the provider API." }),
      ),
      networkTabs(context, "stats"),
    ),
    body,
  );

  let measured: DepthSample[] = [];
  let sampled = 0;
  const depthBody = el("tbody");
  const depthNote = el("p", { class: "stats-note", text: "Measuring a sample…" });

  const paintDepth = (): void => {
    depthBody.replaceChildren();
    const top = [...measured].sort((left, right) => right.titles - left.titles || left.provider.name.localeCompare(right.provider.name)).slice(0, 10);
    const max = Math.max(1, ...top.map((entry) => entry.titles));
    if (top.length === 0) {
      depthBody.append(
        el(
          "tr",
          {},
          el("td", { class: "stats-empty", attrs: { colspan: "4" }, text: "No sampled provider answered." }),
        ),
      );
    }
    for (const entry of top) {
      depthBody.append(
        el(
          "tr",
          {},
          el("th", { attrs: { scope: "row" }, text: entry.provider.name }),
          el("td", { class: "stats-muted", text: categoryTitle(entry.provider.category) }),
          el("td", { class: "stats-number", text: String(entry.titles) }),
          el("td", { class: "stats-bar-cell" }, bar(entry.titles, max)),
        ),
      );
    }
    depthNote.textContent = `Newest listing page 1 at limit ${DEPTH_LIMIT} for ${sampled} sampled working providers; ${measured.length} answered. A sample of ${DEPTH_SAMPLE}, not a catalogue total.`;
  };

  const paint = (providers: ProviderSummary[]): void => {
    const byCategory = new Map<string, number>();
    const byState = new Map<string, number>();
    for (const provider of providers) {
      byCategory.set(provider.category, (byCategory.get(provider.category) ?? 0) + 1);
      byState.set(provider.state, (byState.get(provider.state) ?? 0) + 1);
    }
    const working = providers.filter((provider) => provider.state !== "offline").length;
    const offline = byState.get("offline") ?? 0;
    const categories = [...byCategory.entries()].sort(
      (left, right) => right[1] - left[1] || categoryTitle(left[0]).localeCompare(categoryTitle(right[0])),
    );
    const categoryMax = Math.max(1, ...categories.map(([, count]) => count));

    const figures = el(
      "div",
      { class: "stat-figures" },
      ...[
        { value: providers.length, label: "Providers" },
        { value: working, label: "Working" },
        { value: offline, label: "Offline" },
        { value: categories.length, label: "Categories" },
      ].map((figure) =>
        el(
          "div",
          { class: "stat-figure" },
          el("span", { class: "stat-value", text: String(figure.value) }),
          el("span", { class: "stat-figure-label", text: figure.label }),
        ),
      ),
    );

    const stateBody = el(
      "tbody",
      {},
      ...[
        { label: "Working", value: working, note: "Every provider not marked offline" },
        { label: "Offline", value: offline, note: "state = offline" },
        { label: "Answered ok", value: byState.get("ok") ?? 0, note: "state = ok" },
        { label: "Account required", value: byState.get("auth") ?? 0, note: "state = auth" },
      ].map((row) =>
        el(
          "tr",
          {},
          el("th", { attrs: { scope: "row" }, text: row.label }),
          el("td", { class: "stats-muted", text: row.note }),
          el("td", { class: "stats-number", text: String(row.value) }),
          el("td", { class: "stats-bar-cell" }, bar(row.value, providers.length)),
        ),
      ),
    );

    const categoryBody = el(
      "tbody",
      {},
      ...categories.map(([category, count]) =>
        el(
          "tr",
          {},
          el("th", { attrs: { scope: "row" }, text: categoryTitle(category) }),
          el("td", { class: "stats-number", text: String(count) }),
          el("td", { class: "stats-bar-cell" }, bar(count, categoryMax)),
          el("td", { class: "stats-muted", text: `${Math.round((count / providers.length) * 100)}%` }),
        ),
      ),
    );

    const table = (caption: string, head: string[], rows: HTMLElement): HTMLElement =>
      el(
        "div",
        { class: "stat-scroll" },
        el(
          "table",
          { class: "stat-table" },
          el("caption", { class: "visually-hidden", text: caption }),
          el(
            "thead",
            {},
            el("tr", {}, ...head.map((label) => el("th", { attrs: { scope: "col" }, text: label }))),
          ),
          rows,
        ),
      );

    body.replaceChildren(
      el(
        "section",
        { class: "section stats-section" },
        el(
          "div",
          { class: "section-head" },
          el("h2", { class: "section-title", text: "Catalogue" }),
          el("span", { class: "section-meta", text: "/api/providers" }),
        ),
        figures,
        el("p", {
          class: "stats-note",
          text: "Working counts every provider not marked offline, including the one that asks for an account; the rows below keep the exact states.",
        }),
        table("Provider states in the catalogue", ["State", "Definition", "Providers", "Share"], stateBody),
      ),
      el(
        "section",
        { class: "section stats-section" },
        el(
          "div",
          { class: "section-head" },
          el("h2", { class: "section-title", text: "Providers by category" }),
          el("span", { class: "section-meta", text: `${categories.length} categories` }),
        ),
        table("Providers by category", ["Category", "Providers", "Share", "Percent"], categoryBody),
      ),
      el(
        "section",
        { class: "section stats-section" },
        el(
          "div",
          { class: "section-head" },
          el("h2", { class: "section-title", text: "Titles in the first page" }),
          el("span", { class: "section-meta", text: `sample of ${DEPTH_SAMPLE} providers` }),
        ),
        table("Titles in the first page", ["Provider", "Category", "Titles", "Relative"], depthBody),
        depthNote,
      ),
    );
  };

  paint(context.providers);

  void (async () => {
    try {
      const fresh = await fetchProviders();
      paint(fresh);
    } catch {
      /* The catalogue loaded at boot is still a real API response. */
    }
  })();

  void (async () => {
    const working = context.providers.filter(
      (provider) => provider.state !== "offline" && provider.capabilities.list,
    );
    const sample = spreadSample(working, DEPTH_SAMPLE);
    await eachLimited(sample, 6, async (provider) => {
      const request = withDeadline(NEVER_ABORT, PROVIDER_DEADLINE_MS);
      try {
        const items = await fetchListing(provider.id, { sort: "new", limit: DEPTH_LIMIT }, request.signal);
        measured.push({ provider, titles: items.length });
      } catch {
        /* A silent provider is left out of the ranking rather than scored zero. */
      } finally {
        request.done();
        sampled += 1;
        paintDepth();
      }
    });
    paintDepth();
  })();

  return root;
}
